import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ReactHtmlParser from "react-html-parser";
import moment from "moment";
import { CheckOutlined, CloseOutlined, SendOutlined } from "@ant-design/icons";
import { Button, Input, message, Avatar, Popover } from "antd";
import { UserOutlined } from "@ant-design/icons";
import axios from "axios";
import config from "../../config";

// ---------- localStorage safe helpers ----------
const safeJSONParse = (value, fallback = null) => {
  try {
    if (!value || value === "undefined" || value === "null") return fallback;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const safeGetItem = (key) => {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    console.warn(`localStorage.getItem failed for key="${key}"`, e);
    return null;
  }
};

const NewsCard = (props) => {
  const [comment, setComment] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // ✅ Safe parse so corrupted localStorage won't crash the whole page
  const user = useMemo(
    () => safeJSONParse(safeGetItem("user"), null), // ✅ Safe nested call
    []
  );

  const isLoggedIn = Boolean(user && user._id);
  const canCommentVote = isLoggedIn;

  const post = props?.post || {};

  // ✅ "has user already voted?" booleans
  const userUpVoted = isLoggedIn ? (post?.upvotes || []).includes(user._id) : false;
  const userDownVoted = isLoggedIn ? (post?.downvotes || []).includes(user._id) : false;

  // block if they voted either way
  const canVote = userUpVoted || userDownVoted;

  const upVoteCount = (post?.upvotes?.length || 0).toLocaleString("en-US");
  const downVoteCount = (post?.downvotes?.length || 0).toLocaleString("en-US");

  async function postUpdatePost(id, updatedPost, successMessage) {
    try {
      setIsLoading(true);

      const accessToken = safeGetItem("access_token"); // ✅ Safe access

      await axios.patch(`${config.BLOGS}/blogs/update/${id}`, updatedPost, {
        headers: { token: accessToken },
      });

      // ✅ update list locally (NewsPage owns `data`)
      if (typeof props.onPostUpdated === "function") {
        props.onPostUpdated(updatedPost);
      }

      message.success(successMessage);
    } catch (err) {
      console.log(err);
      message.warning("Something went wrong‼️");
    } finally {
      setIsLoading(false);
    }
  }

  // Add comment
  async function addCommentToPost(event) {
    event.preventDefault();

    if (!isLoggedIn) {
      message.info("Please login to use this feature.");
      return;
    }

    const newComment = {
      comment,
      _id: user._id,
      nickname: user.nickname || user.name || "User",
      picture: user.picture || "",
    };

    const updatedPost = {
      ...post,
      comments: [newComment, ...(post.comments || [])],
    };

    await postUpdatePost(post._id, updatedPost, "Comment posted!");
    setComment("");
  }

  // Vote
  async function voteInPost(event, type) {
    event.preventDefault();

    if (!isLoggedIn) {
      message.info("Please login to use this feature.");
      return;
    }

    if (canVote) {
      message.info("You have voted already!");
      return;
    }

    const updatedPost = {
      ...post,
      upvotes:
        type === "upvote" ? [user._id, ...(post.upvotes || [])] : (post.upvotes || []),
      downvotes:
        type === "downvote" ? [user._id, ...(post.downvotes || [])] : (post.downvotes || []),
    };

    await postUpdatePost(post._id, updatedPost, "Voted Successfully!");
  }

  function notLoggedIn(event) {
    event.preventDefault();
    message.info("Please login to use this feature.");
  }

  const popoverContent = !canCommentVote
    ? "Please signup in order to vote"
    : canVote
    ? "You have voted!"
    : "";

  const PopoverWrapper = ({ children }) => (
    <Popover
      content={popoverContent}
      trigger={!canCommentVote || canVote ? "hover" : undefined}
    >
      {children}
    </Popover>
  );

  return (
    <div className="BlogGridItem">
      <div className="CardColumn">
        <div className="CardMain">
          <Link
            to={{
              pathname: props.pathname,
              state: {
                data: post,
                fromCategory: props.currentCategory,
                fromFilters: {
                  ...props.inputState,
                  state: props.inputState?.state?.trim() || "",
                  district: props.inputState?.district?.trim() || "",
                },
                fromScrollY: window.scrollY,
              },
            }}
            className="link CardLink"
          >
            <img
              className="CardImage"
              src={post.coverImage || "https://s3.amazonaws.com/dev.nocap.com/uploads/logo.png"}
              alt={post.title}
            />

            {post.videoUrl ? (
              <iframe
                src={post.videoUrl}
                frameBorder="0"
                style={{ width: "100%", height: "100%" }}
                allow="encrypted-media"
                allowFullScreen
                title="video"
              />
            ) : null}

            <div className="actionContainer">
              <PopoverWrapper>
                <Button
                  className="voteButton voteUp"
                  onClick={(e) => (isLoggedIn ? voteInPost(e, "upvote") : notLoggedIn(e))}
                  icon={<CheckOutlined />}
                  disabled={isLoggedIn ? canVote : true}
                >
                  <span className="voteLabel">Upvotes</span>
                  <span className="voteCount">{upVoteCount}</span>
                </Button>
              </PopoverWrapper>

              <PopoverWrapper>
                <Button
                  className="voteButton voteDown"
                  onClick={(e) => (isLoggedIn ? voteInPost(e, "downvote") : notLoggedIn(e))}
                  icon={<CloseOutlined />}
                  disabled={isLoggedIn ? canVote : true}
                >
                  <span className="voteLabel">Downvotes</span>
                  <span className="voteCount">{downVoteCount}</span>
                </Button>
              </PopoverWrapper>
            </div>

            <div className="title">
              <h3>{post.title}</h3>
            </div>

            {post.created_at && (
              <div className="date">
                {moment(post.created_at).format("MMMM DD, YYYY")}
              </div>
            )}

            {post.author && <div className="origin">Author: {post.author}</div>}

            <div className="body">
              {post.body ? ReactHtmlParser(`${post.body.substring(0, 150)} ...`) : ""}
            </div>

            <div className="noCapSection">
              <div className="noCapBody">
                <div className="body">{post.editorial}</div>
              </div>
            </div>
          </Link>

          <div className="commentsContainer">
            <div className="title">
              <h3>Comments</h3>
            </div>

            <div className="commentsBody">
              {(post.comments || []).slice(0, 4).map((c, idx) => (
                <div className="body" key={idx}>
                  <strong>{c.nickname}</strong> {c.comment}
                </div>
              ))}

              {(post.comments || []).length > 4 && (
                <Link
                  to={{ pathname: props.pathname, state: { data: post } }}
                  className="link"
                  style={{ display: "block", textAlign: "center" }}
                >
                  View all ({(post.comments || []).length}) comments
                </Link>
              )}
            </div>

            {isLoggedIn && (
              <Input.Group
                className="commentsInputContainer"
                compact
                style={{ display: "flex", justifyContent: "space-around" }}
              >
                <Input
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onClick={(e) => e.preventDefault()}
                  placeholder="Type to add comment"
                  bordered={false}
                />
                <Button
                  size="large"
                  icon={<SendOutlined />}
                  disabled={isLoading || !Boolean(comment)}
                  onClick={(e) => addCommentToPost(e)}
                />
              </Input.Group>
            )}
          </div>
        </div>

        <footer className="FactCheckFooter">
          <div className="title">
            <b>Fact Check</b>
          </div>

          <div className="candidateAvatars">
            {post.factCheck?.length > 0 ? (
              post.factCheck.slice(0, 3).map((article) => (
                <a
                  key={article.link}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="factLink"
                >
                  <Avatar
                    size="large"
                    src={article.imageLink}
                    style={{ marginRight: 12 }}
                    icon={!article.imageLink ? <UserOutlined /> : undefined}
                  />
                  <span className="factSource">{article.newsPage}</span>
                </a>
              ))
            ) : (
              <div className="noFacts">No fact checks provided.</div>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default NewsCard;